import { z } from 'zod';

import { jobProfileSkillSchema } from '../../../../shared/source/data-contract/skill';
import { FactoryAutoRewindSequence } from '../../../../shared/source/data-factory/factory-auto-rewind-sequence';
import { jobProfileSkillFactory } from '../../../../shared/source/data-factory/skill.factories';

import type { HttpFetcherError } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { JobProfileForGenerationWithPrompt } from '../../job-profile/job-profile-schema';
import type Maybe from 'true-myth/maybe';
import type Result from 'true-myth/result';

export const redetectSkillsWithPromptSchema = z.object({
    addedSkills: jobProfileSkillSchema.array(),
    changedSkills: jobProfileSkillSchema.array(),
    detectedSkills: jobProfileSkillSchema.array(),
    removedSkills: jobProfileSkillSchema.array(),
});

export type SkillsDetectionResult = z.infer<typeof redetectSkillsWithPromptSchema>;

export const jobProfileSkillDetectionResponseFactory = FactoryAutoRewindSequence.define<SkillsDetectionResult>(() => ({
    addedSkills: [jobProfileSkillFactory.build()],
    changedSkills: [jobProfileSkillFactory.build()],
    detectedSkills: [jobProfileSkillFactory.build()],
    removedSkills: [jobProfileSkillFactory.build()],
}));

export type RedetectSkillsRequestPayload = {
    readonly additionalPrompt: Maybe<string>;
    readonly detectionType: 'adjust' | 'redetect';
    readonly jobProfile: JobProfileForGenerationWithPrompt;
    readonly jobProfileId: string;
};

export type RedetectSkillsRequest = (
    payload: RedetectSkillsRequestPayload,
    abortSignal?: AbortSignal,
) => Promise<Result<SkillsDetectionResult, HttpFetcherError>>;

export const createRedetectSkillsRequest = (apiFetcher: ValidatingHttpFetcherWithBaseUrl): RedetectSkillsRequest => {
    const redetectSkillsRequest = async (
        payload: RedetectSkillsRequestPayload,
        abortSignal?: AbortSignal,
    ): Promise<Result<SkillsDetectionResult, HttpFetcherError>> => {
        const { additionalPrompt, detectionType, jobProfile, jobProfileId } = payload;

        const requestPayload = {
            additionalPrompt: {
                type: detectionType,
                userInput: additionalPrompt.unwrapOr(undefined),
            },
            jobProfile,
        };

        return apiFetcher.postJson(redetectSkillsWithPromptSchema, {
            pathname: `/job-profiles/${jobProfileId}/detect-skills`,
            payload: requestPayload,
            requestOptions: {
                signal: abortSignal,
            },
        });
    };

    return redetectSkillsRequest;
};
